import React, { useState } from 'react';
import useRoute from '@/Hooks/useRoute';
import AppLayout from '@/Layouts/AppLayout';
import FilePills from '@/Components/FilePills';
import SnippetViewer, {
  SnippetViewerProps,
} from '@/Pages/Files/Partials/SnippetViewer';
import BackTo from '@/Components/BackTo';
import Avatar from '@/Components/Avatar';
import ModeInline from '@/Components/ModeInline';
import PackageName from '@/Pages/Packages/Partials/PackageName';
import ProjectName from '@/Pages/Projects/Partials/ProjectName';
import { Link } from '@inertiajs/react';
import SidebarSection from '@/Components/Sidebar/SidebarSection';
import Comments from '@/Components/Comments';
import CommentSubmit from '@/Components/CommentSubmit';
import useTypedPage from '@/Hooks/useTypedPage';
import Accordion from '@/Components/Accordion';
import IconAi from '@/svgs/stars-lg.svg?react';
import { Tooltip } from '@/Components/Tooltip';
import Badge from '@/Components/Badge';
import { Auth } from '@/types';
import { cn } from '@/utils';

const Sidebar = (props: SnippetViewerProps) => {
  const route = useRoute();
  const { auth, file } = useTypedPage<{ auth: Auth }>().props;
  const isSnippetViewVisible = route().current('snippet.show', {
    file: file?.id,
  });
  const [hideDetails, setHideDetails] = useState(true);

  function showDetails() {
    if (hideDetails) {
      setHideDetails(false);
    }
  }

  return (
    <div
      className={cn(
        'h-full',
        {
          'cursor-pointer': hideDetails,
        },
        {
          'fixed right-0 z-20 dark:bg-brand-dark-75 lg:relative':
            isSnippetViewVisible,
        }
      )}
      onClick={() => showDetails()}
    >
      <div
        className="block min-w-[26px] cursor-pointer lg:hidden"
        onClick={() => setHideDetails(!hideDetails)}
      >
        {hideDetails ? (
          <div className="absolute origin-bottom-left rotate-90 transform whitespace-nowrap">
            Show Details
          </div>
        ) : (
          <div className="pr-4 pt-2 text-right">Hide details</div>
        )}
      </div>
      <div
        className={cn(
          ' relative h-full w-full max-w-96 flex-col space-y-4 px-8 pb-12 pt-8 lg:flex lg:min-w-[26rem]',
          { hidden: hideDetails }
        )}
      >
        <h2 className="font-medium dark:text-brand-light-100">
          Snippet Information
        </h2>

        {props.package?.project && (
          <SidebarSection title="Associated project">
            <ProjectName project={props.package.project} />
          </SidebarSection>
        )}

        {props.package && (
          <SidebarSection title="Package">
            <PackageName package={props.package} />
          </SidebarSection>
        )}

        {props.files.length > 0 && (
          <SidebarSection title="Related public snippets">
            <FilePills files={props.files} className="my-2" />
          </SidebarSection>
        )}

        {props.file?.mode && (
          <SidebarSection title="Mode">
            <Link href={route('modes.show', props.file.mode)}>
              <ModeInline mode={props.file.mode} />
            </Link>
          </SidebarSection>
        )}

        {props.package.owner && (
          <SidebarSection title="Author">
            <Avatar user={props.package.owner} />{' '}
          </SidebarSection>
        )}

        {props.comments.data.length > 0 && (
          <h2 className="flex justify-between font-medium dark:text-brand-light-100">
            Comments
            <Badge className="ml-2" size="sm">
              Pro
            </Badge>
          </h2>
        )}
        <div className="max-w-[352px] grow overflow-y-scroll">
          <Comments data={props.comments} />
        </div>
        {auth.user &&
          (auth.user.current_team?.billing.plan !== 'free' ||
            auth.user.id === props.file?.owner?.id) && (
            <CommentSubmit fileId={props.file.id} />
          )}
      </div>
    </div>
  );
};

export default function Snippet(props: SnippetViewerProps) {
  const route = useRoute();
  const { faq } = useTypedPage<{ faq: App.Data.FaqData[] }>().props;
  const { jsonLD } = useTypedPage().props;

  return (
    <AppLayout
      mobileSupported={true}
      title={`Snippet – ${props.file.name}`}
      renderHeader={() => (
        <BackTo
          label="Back to public snippets"
          route={route('snippets.index')}
          forcedUrlDomain="website"
        />
      )}
      renderSidebar={() => <Sidebar {...props} />}
      jsonLD={jsonLD}
    >
      <div className="w-full">
        <SnippetViewer {...props} />
      </div>
      {faq && faq.length > 0 && (
        <div className="mx-8 border-t pt-10 dark:border-brand-dark-50">
          <h2 className="pb-6 font-medium text-white">
            Frequently Asked Questions
            <Tooltip
              message={
                <p className="max-w-[250px] leading-relaxed">
                  The following FAQ's are AI generated and may not be
                  completely accurate.{' '}
                  <a
                    href="https://intercom.help/wpai/en/articles/8705007-ai-generated-faqs"
                    target="_blank"
                    className="font-bold text-brand hover:text-brand-dark-100 dark:text-brand-dark dark:hover:text-brand-light-100"
                  >
                    Learn More
                  </a>
                </p>
              }
            >
              <sup className="opacity-50 hover:opacity-100">
                <IconAi className="mr-2 inline-block h-5 w-5" />
              </sup>
            </Tooltip>
          </h2>
          <Accordion
            items={faq.map(i => {
              return { header: i.question, content: i.answer };
            })}
          />
        </div>
      )}
      <div className="w-full px-8 pb-12">
        {props.files.length > 0 && (
          <div>
            <h3 className="">Related public snippets</h3>
            <FilePills files={props.files} className="my-2" />
          </div>
        )}
      </div>
    </AppLayout>
  );
}
